/**
 * Types for the attachment upload API and client-side upload queue.
 */

// --- API response types ---

/** Response from POST /v2/attachments */
export type CreateAttachmentResponse = {
  id: string;
  name: string;
  bucket: string;
  path: string;
  service_provider: string;
  /** Presigned S3 PUT URL — expires in 5 minutes */
  url: string;
  created_at: string;
  updated_at: string;
};

/** Single attachment returned by the attachments list endpoint */
export type Attachment = {
  id: string;
  name: string;
  bucket: string;
  path: string;
  owner_type: string;
  owner_reference: string;
  service_provider: string;
  upload_status: string;
  url: string;
  created_at: string;
  updated_at: string;
};

/** Response from GET /vip/v2/INVOICE/:id/attachments */
export type AttachmentListResponse = {
  attachments: Attachment[];
  owner_reference: string;
  owner_type: string;
};

// --- Client-side upload tracking ---

export const FILE_UPLOAD_STATUS = {
  QUEUED: "queued",
  CREATING: "creating",
  UPLOADING: "uploading",
  UPLOADED: "uploaded",
  FAILED: "failed",
} as const;

export type FileUploadStatus = (typeof FILE_UPLOAD_STATUS)[keyof typeof FILE_UPLOAD_STATUS];

export type FileUploadItem = {
  file: File;
  status: FileUploadStatus;
  /** Upload progress percentage (0-100), only meaningful during "uploading" */
  progress: number;
  /** Set after step 1 (createAttachment) succeeds */
  attachmentId?: string;
  /** Error message, set when status is "failed" */
  error?: string;
};

export type UploadQueueSummary = {
  total: number;
  queued: number;
  uploading: number;
  uploaded: number;
  failed: number;
};

// --- Queue configuration ---

export const DEFAULT_ALLOWED_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
] as const;

/** MIME → file-extension mapping, used to derive the <input accept> attribute.
 *  Keyed on DEFAULT_ALLOWED_TYPES so a new MIME cannot be added without its extensions. */
const MIME_TO_EXTENSIONS: Record<(typeof DEFAULT_ALLOWED_TYPES)[number], string[]> = {
  "application/pdf": [".pdf"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
};

/** File extensions for `<input accept>`, derived from DEFAULT_ALLOWED_TYPES. */
export const ACCEPT_ATTR = DEFAULT_ALLOWED_TYPES.flatMap((mime) => MIME_TO_EXTENSIONS[mime]).join(",");

export const DEFAULT_MAX_FILE_SIZE_MB = 15;
export const DEFAULT_MAX_FILE_SIZE = DEFAULT_MAX_FILE_SIZE_MB * 1024 * 1024;
export const DEFAULT_MAX_FILES = 50;
export const DEFAULT_CONCURRENCY = 3;

export type UploadQueueOptions = {
  concurrency?: number;
  maxFileSize?: number;
  maxFiles?: number;
  allowedTypes?: string[];
  onItemUpdate?: (index: number, update: Partial<FileUploadItem>) => void;
};

export type FileValidationError = {
  file: File;
  reason: string;
};

export type AddFilesResult = {
  accepted: number;
  rejected: FileValidationError[];
};

// --- Batch processing (OCR) ---

export const PROCESSING_STATUS = {
  OCR_IN_PROGRESS: "OCR_IN_PROGRESS",
  OCR_COMPLETED: "OCR_COMPLETED",
  OCR_FAILED: "OCR_FAILED",
  ALREADY_PROCESSED: "ALREADY_PROCESSED",
  VALIDATION_IN_PROGRESS: "VALIDATION_IN_PROGRESS",
  VALIDATION_SUCCESS: "VALIDATION_SUCCESS",
  VALIDATION_FAILED: "VALIDATION_FAILED",
} as const;

export type ProcessingStatus = (typeof PROCESSING_STATUS)[keyof typeof PROCESSING_STATUS];

/** Response from POST /v2/vip/invoices/process-batch */
export type StartBatchResponse = {
  /** BE is renaming workflow_id → batch_id; both kept during transition */
  batch_id?: string;
  workflow_id?: string;
  run_id: string;
  started_at: string;
};

// --- SSE event types ---

export const BATCH_EVENT_TYPE = {
  BATCH_STARTED: "batch_started",
  LIFECYCLE: "lifecycle",
  CHILD_RESULT: "child_result",
  BATCH_COMPLETE: "batch_complete",
} as const;

export type BatchEventType = (typeof BATCH_EVENT_TYPE)[keyof typeof BATCH_EVENT_TYPE];

export type BatchStartedPayload = {
  batch_id: string;
  total: number;
};

/** BE sends a flat StreamEvent struct for all event types. lifecycle carries transition details. */
export type BatchLifecyclePayload = {
  batch_id: string;
  attachment_id: string;
  invoice_id?: number;
  from_status: string;
  to_status: string;
  version?: number;
  /** Failure or skip reason, if applicable */
  reason?: string;
};

export type BatchChildResultPayload = {
  attachment_id: string;
  /** Invoice created for this attachment (0 if creation failed before invoice row was written) */
  invoice_id?: number;
  status: ProcessingStatus;
  /** Failure/skip reason from the backend */
  reason?: string;
};

export type BatchCompletePayload = {
  batch_id: string;
  total?: number;
  succeeded: number;
  failed: number;
  skipped: number;
};

/** Per-child status in a batch polling/SSE response.
 *  During SSE: status may be an intermediate lifecycle status (e.g. OCR_IN_PROGRESS)
 *  until child_result arrives and sets the terminal ProcessingStatus. */
export type BatchChildStatus = {
  attachment_id: string;
  invoice_id?: number;
  /** Terminal ProcessingStatus or intermediate lifecycle status string */
  status: string;
  /** Failure/skip reason from the backend */
  reason?: string;
  /** Polling endpoint's name for the same text; getBatchStatus copies it into `reason`. */
  error?: string;
};

/** Response from GET /v2/vip/invoices/process-batch/:batch_id/status */
export type BatchStatusResponse = {
  batch_id: string;
  total: number;
  pending: number;
  succeeded: number;
  failed: number;
  skipped: number;
  done: boolean;
  children: BatchChildStatus[];
};

/** Single batch entry from GET /v2/vip/invoices/process-batch */
export type ActiveBatch = {
  batch_id: string;
  started_at: string;
  state: string;
  total: number;
  pending: number;
  succeeded: number;
  failed: number;
  skipped: number;
  done: boolean;
};

/** Response from GET /v2/vip/invoices/process-batch */
export type ActiveBatchesResponse = {
  batches: ActiveBatch[];
  has_more: boolean;
};

// --- Multi-batch manager types ---

/** Lightweight snapshot of a file after it has been uploaded — no File object reference. */
export type BatchFileSnapshot = {
  name: string;
  attachmentId: string;
};

/** Single entry in the batch manager — one SSE stream per entry. */
export type BatchEntry = {
  batchId: string;
  /** Snapshot of files sent to this batch (empty when batch is restored from BE on refresh). */
  files: BatchFileSnapshot[];
  /** Latest aggregated status from the SSE stream or polling fallback. */
  streamData: BatchStatusResponse | null;
  done: boolean;
  /** True when SSE returned 503 and batch fell back to polling. */
  useFallback: boolean;
};

// --- Upload flow phases ---

export type FlowPhase = "idle" | "selected" | "uploading" | "processing" | "complete";
